import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, RefreshCw } from 'lucide-react';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { adminListUsers, adminUpsertUser, adminDeleteUser } from '../../utils/database';
import type { AdminUserRecord } from '../../utils/database';
const ACCESS_LEVELS: AdobeImsAccessLevel[] = ['unregistered', 'viewer', 'editor', 'admin', 'superadmin'];
const ACCESS_LEVEL_COLORS: Record<string, string> = {
  viewer: 'bg-slate-100 text-slate-700',
  editor: 'bg-blue-100 text-blue-700',
  admin: 'bg-purple-100 text-purple-700',
  superadmin: 'bg-amber-100 text-amber-800'
};
function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}
export function UsersTab() {
  const [users, setUsers] = useState<AdminUserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<AdminUserRecord | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    accessLevel: 'viewer' as AdobeImsAccessLevel
  });
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminListUsers();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await adminUpsertUser({
        email: formData.email,
        name: formData.name || undefined,
        accessLevel: formData.accessLevel
      });
      setShowAddForm(false);
      setEditingUser(null);
      setFormData({
        email: '',
        name: '',
        accessLevel: 'viewer'
      });
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };
  const handleDelete = async (email: string) => {
    setSaving(true);
    setError(null);
    try {
      await adminDeleteUser(email);
      setConfirmDelete(null);
      await loadUsers();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setSaving(false);
    }
  };
  const startEdit = (user: AdminUserRecord) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name || '',
      accessLevel: user.accessLevel
    });
    setShowAddForm(true);
  };
  const columns: SortableColumn<AdminUserRecord>[] = [{
    key: 'email',
    label: 'Email',
    width: '25%',
    render: row => <span className="font-medium text-slate-900" data-id="element-284" data-mp="1hxtxz8b1wp27">{row.email}</span>,
    sortValue: row => row.email
  }, {
    key: 'name',
    label: 'Name',
    width: '18%',
    render: row => <span className="text-slate-600" data-id="element-285" data-mp="17zedho6j4e1o">{row.name || '—'}</span>,
    sortValue: row => row.name || ''
  }, {
    key: 'accessLevel',
    label: 'Access Level',
    width: '12%',
    render: row => <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${ACCESS_LEVEL_COLORS[row.accessLevel] || 'bg-slate-100 text-slate-700'}`} data-id="element-286" data-mp="z6j7gs98meau">
          {row.accessLevel}
        </span>,
    sortValue: row => row.accessLevel
  }, {
    key: 'createdAt',
    label: 'Created',
    width: '15%',
    render: row => <span className="text-slate-500 text-xs" data-id="element-287" data-mp="k90knvg4eb61">{formatDate(row.createdAt)}</span>,
    sortValue: row => row.createdAt || ''
  }, {
    key: 'lastLoginAt',
    label: 'Last Login',
    width: '15%',
    render: row => <span className="text-slate-500 text-xs" data-id="element-288" data-mp="19xxwk5zojtb5">{formatDate(row.lastLoginAt)}</span>,
    sortValue: row => row.lastLoginAt || ''
  }, {
    key: 'actions',
    label: '',
    width: '10%',
    render: row => <div className="flex items-center gap-1" data-id="element-289" data-mp="107crbzpi3qj9">
          <button onClick={e => {
        e.stopPropagation();
        startEdit(row);
      }} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-indigo-600 transition-colors" title="Edit user" data-id="element-290" data-mp="1061kts4o84og">
            <Pencil size={14} data-id="element-291" data-mp="d86pz8e46foz" />
          </button>
          <button onClick={e => {
        e.stopPropagation();
        setConfirmDelete(row.email);
      }} className="p-1 rounded hover:bg-slate-100 text-slate-400 hover:text-red-600 transition-colors" title="Delete user" data-id="element-292" data-mp="d9hyflii8bmf">
            <Trash2 size={14} data-id="element-293" data-mp="2bnkvu6ohtbxk" />
          </button>
        </div>
  }];
  return <div className="space-y-4" data-id="element-294" data-mp="3w4u4bvwvwhi2">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-295" data-mp="3w39881ux8qi3">
        <div data-id="element-296" data-mp="3s4rzjdvur7yk">
          <h3 className="text-base font-semibold text-slate-900" data-id="element-297" data-mp="3s51oqz92pb37">Users</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-298" data-mp="9q0wu2rkif1o">
            Manage user accounts and access levels ({users.length} users)
          </p>
        </div>
        <div className="flex items-center gap-2" data-id="element-299" data-mp="4x7svh3xbaqn">
          <button onClick={() => void loadUsers()} disabled={loading} className="p-2 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50" title="Refresh" data-id="element-300" data-mp="jmvvqmrr6hgh">
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} data-id="element-301" data-mp="nps6jrb1x99v" />
          </button>
          <button onClick={() => {
          setEditingUser(null);
          setFormData({
            email: '',
            name: '',
            accessLevel: 'viewer'
          });
          setShowAddForm(true);
        }} className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 transition-colors" data-id="element-302" data-mp="2ug056dciyq4t">
            <Plus size={14} data-id="element-303" data-mp="yc22bjfigw6b" />
            Add User
          </button>
        </div>
      </div>

      {/* Error */}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-id="element-304" data-mp="13ade3zpd9gr6">
          {error}
        </div>}

      {/* Add/Edit Form */}
      {showAddForm && <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3" data-id="element-305" data-mp="1cc3f9x91vu25">
          <h4 className="text-sm font-semibold text-slate-700" data-id="element-306" data-mp="1haeoq6vfo2ju">
            {editingUser ? 'Edit User' : 'Add New User'}
          </h4>
          <div className="grid grid-cols-3 gap-3" data-id="element-307" data-mp="1m4iuwnwvk6jv">
            <div data-id="element-308" data-mp="1r0hezzb672eh">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-309" data-mp="1r0zk7lft8qyj">Email *</label>
              <input type="email" value={formData.email} onChange={e => setFormData(p => ({
            ...p,
            email: e.target.value
          }))} disabled={!!editingUser} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-slate-100 disabled:text-slate-500" placeholder="user@adobe.com" data-id="element-310" data-mp="1v4fcy6ore75y" />
            </div>
            <div data-id="element-311" data-mp="36m1hp3gy7uu8">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-312" data-mp="36mjogu18lrge">Name</label>
              <input type="text" value={formData.name} onChange={e => setFormData(p => ({
            ...p,
            name: e.target.value
          }))} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" placeholder="Display name" data-id="element-313" data-mp="74jzlctq6lg6" />
            </div>
            <div data-id="element-314" data-mp="qvf7waw86f9i">
              <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-315" data-mp="m2m3xp8kzayh">Access Level *</label>
              <select value={formData.accessLevel} onChange={e => setFormData(p => ({
            ...p,
            accessLevel: e.target.value as AdobeImsAccessLevel
          }))} className="w-full px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-316" data-mp="3qkp2lf3hfoas">
                {ACCESS_LEVELS.map(level => <option key={level} value={level} data-id="element-317" data-mp="d50x528xrxbb">{level}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1" data-id="element-318" data-mp="i43rnsrail51">
            <button onClick={() => void handleSave()} disabled={saving || !formData.email} className="px-4 py-1.5 rounded-md bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 transition-colors" data-id="element-319" data-mp="45du6c0k6jg5">
              {saving ? 'Saving...' : editingUser ? 'Update' : 'Create'}
            </button>
            <button onClick={() => {
          setShowAddForm(false);
          setEditingUser(null);
        }} className="px-4 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-320" data-mp="2ld5navs2khpc">
              Cancel
            </button>
          </div>
        </div>}

      {/* Table */}
      {loading && users.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-321" data-mp="22mo0t8xtyicf">
          Loading users...
        </div> : <SortableTable columns={columns} data={users} rowKey={row => row.email} defaultSort={{
      key: 'email',
      dir: 'asc'
    }} data-id="element-322" data-mp="1xomg6jkraayu" />}

      {/* Delete Confirmation */}
      {confirmDelete && <div className="fixed inset-0 z-60 flex items-center justify-center" style={{
      background: 'rgba(0,0,0,0.4)'
    }} data-id="element-323" data-mp="27jwjdamw4oe7">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-324" data-mp="1exbs0a6f0can">
            <h4 className="text-base font-semibold text-slate-900" data-id="element-325" data-mp="3jkmwb6mic5bc">Delete User</h4>
            <p className="mt-2 text-sm text-slate-600" data-id="element-326" data-mp="3fj1er7z132ut">
              Are you sure you want to delete <strong data-id="element-327" data-mp="3ajopyholjlpl">{confirmDelete}</strong>? This action cannot be undone.
            </p>
            <div className="mt-4 flex items-center gap-2 justify-end" data-id="element-328" data-mp="3alhwlwz7m5xl">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-1.5 rounded-md border border-slate-300 text-sm font-medium text-slate-600 hover:bg-slate-50 transition-colors" data-id="element-329" data-mp="35kc5u4p40sqp">
                Cancel
              </button>
              <button onClick={() => void handleDelete(confirmDelete)} disabled={saving} className="px-4 py-1.5 rounded-md bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-50 transition-colors" data-id="element-330" data-mp="2rtyudsd0evwm">
                {saving ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}